import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { researchApi } from "../api";
import type { ResearchFilters } from "../routes";
import { looksLikeCitation, sourceById } from "../sources";
import type { ResearchSourceId } from "../types";

/** Library answers are public law: they hold for a while. */
const STALE = 10 * 60_000;

export const useLibrarySearch = (
  source: ResearchSourceId,
  query: string,
  options: { limit?: number; filters?: ResearchFilters; enabled?: boolean } = {}
) => {
  const { limit = 10, filters = {}, enabled = true } = options;
  const browsable = Boolean(sourceById(source).browsable);
  return useQuery({
    queryKey: ["research", "search", source, query, limit, filters],
    queryFn: () => researchApi.search(source, query, { limit, filters }),
    enabled: enabled && (query.length > 0 || browsable),
    staleTime: STALE,
    retry: false,
    placeholderData: keepPreviousData
  });
};

/** Resolves the query when it reads as a citation; idle otherwise. */
export const useCitation = (query: string) =>
  useQuery({
    queryKey: ["research", "citation", query],
    queryFn: () => researchApi.citation(query),
    enabled: looksLikeCitation(query),
    staleTime: STALE,
    retry: false
  });

export const useLibraryDocument = (id: string | null) =>
  useQuery({
    queryKey: ["research", "document", id],
    queryFn: () => researchApi.document(id ?? ""),
    enabled: Boolean(id),
    staleTime: STALE,
    retry: false
  });

/** The passages in one document that bear on the search that found it. */
export const usePassages = (query: string, documentId: string | null) =>
  useQuery({
    queryKey: ["research", "passages", documentId, query],
    queryFn: () => researchApi.passages(query, documentId ?? ""),
    enabled: Boolean(documentId) && query.length > 0,
    staleTime: STALE,
    retry: false
  });

export const useCoverage = () =>
  useQuery({
    queryKey: ["research", "coverage"],
    queryFn: researchApi.coverage,
    staleTime: 60 * 60_000,
    retry: false
  });
